import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Users as UsersIcon,
  UserCog,
  Activity,
  Monitor,
  FolderTree,
  ScrollText,
  SquareTerminal,
  LayoutDashboard,
  Key,
  Webhook,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Optional section break above this item */
  sectionLabel?: string;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/sessions', label: 'Sessions', icon: Activity },
  { to: '/devices', label: 'Devices', icon: Monitor },
  { to: '/address-books', label: 'Address books', icon: FolderTree },
  { to: '/users', label: 'Users', icon: UsersIcon, sectionLabel: 'Administration' },
  { to: '/groups', label: 'Groups', icon: UserCog },
  { to: '/audit', label: 'Audit log', icon: ScrollText },
  { to: '/tokens', label: 'API tokens', icon: Key, sectionLabel: 'Integrations' },
  { to: '/outbound-webhooks', label: 'Webhooks', icon: Webhook },
];

export function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <aside className="hidden md:flex md:w-60 md:flex-col md:border-r md:bg-card/40">
      {/* Brand block — editorial serif title sets the tone */}
      <div className="flex h-14 items-center gap-2.5 border-b px-5">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <SquareTerminal className="h-4 w-4" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="font-display text-lg italic tracking-tight">WeRemote</span>
          <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
            console
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col gap-0.5 p-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = pathname.startsWith(item.to);
          return (
            <React.Fragment key={item.to}>
              {item.sectionLabel && (
                <p className="mt-4 mb-1 px-3 font-mono text-2xs uppercase tracking-widest text-muted-foreground/60">
                  {item.sectionLabel}
                </p>
              )}
              <Link
                to={item.to}
                className={cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </Link>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t p-4">
        <p className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
          v0.1.0 · lot 2G
        </p>
      </div>
    </aside>
  );
}
